// The campaign's NPCs at the table (issue 1056): the DM's list of the campaign's NPC pool (kept on the site, on the
// campaign's page), searchable by name, each row with Place — the compendium's "Place <name>" dialog, hidden ticked,
// landing at the first free square near the centre of the DM's view. An NPC with a stat block comes linked to it with
// its numbers; one without comes as a plain token named after it. With no NPCs, the list says where to add them: the
// campaign's page on the site, opened in the second window the compendium uses.
//
// Issue 1585, slice 4: an NPC whose stat block the site still has raises that entry's hover card as a Compendium row
// does (`useEntryCard`: hover intent, keyboard focus, a touch hold; Escape, leaving or a press elsewhere closes it), and
// its name opens the stat block's page in the compendium window. The card's buttons are Open, ★ Star / Unstar (the DM's
// site favourites, read once when the first card is raised; Star waits for that read and stays unusable if it fails)
// and Place, which is the row's own Place: the NPC itself, by its id, never a bare monster.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useShell } from '../context';
import { useOptionalTableState } from '../../table/TableState';
import { campaignPageUrl, listNpcs, npcsMatching, type CampaignNpc } from '../../npcs';
import { compendiumHome, setFavourite } from '../../compendium';
import { siteUrl } from '../../session';
import { COMPENDIUM_WINDOW } from './CompendiumPanel';
import { useEntryCard, type RowPreview } from './CompendiumParts';
import { EntryCard } from './EntryCard';
import { PlaceDialog } from './PlaceDialog';
import { BTN } from '../ui';

type Shown = { kind: 'loading' } | { kind: 'error'; reason: string } | { kind: 'ok'; campaignId: string; npcs: CampaignNpc[] };
const DOWN = "The campaign's NPCs can't be read just now. Try again in a moment.";
const btn = `${BTN} shrink-0`;
/** The DM's site favourites by address, while they are read, or unreadable. */
type Stars = ReadonlySet<string> | 'loading' | 'unknown';
const addressOf = (a: { segment: string; slug: string }) => `${a.segment}/${a.slug}`;

/** One NPC: its name (a button that opens the stat block's page when it has one), its stat block, Place. */
function NpcRow({ n, mayPlace, onPlace, onOpen, preview }: { n: CampaignNpc; mayPlace: boolean; onPlace: () => void; onOpen?: (() => void) | undefined; preview?: RowPreview | undefined }) {
  const { li, button, rowProps, buttonProps, clicked } = useEntryCard(preview);
  const label = <>
    <span className="text-stone-100">{n.name}</span>
    <span className="text-stone-500">{' · '}{n.hasStatBlock ? (n.statBlockName ?? 'stat block no longer on the site') : 'no stat block'}</span>
  </>;
  return (
    <li ref={li} data-testid={`npc-${n.id}`} className="flex items-center gap-2 py-1.5" {...rowProps}>
      {onOpen ? (
        <button ref={button} type="button" className="min-w-0 flex-1 text-left hover:bg-stone-800" title="Opens the stat block's page in the compendium window"
          onClick={() => clicked(onOpen)} {...buttonProps}>{label}</button>
      ) : <span className="min-w-0 flex-1">{label}</span>}
      <button type="button" className={btn} aria-label={`Place ${n.name} on the board`} data-testid={`npc-place-${n.id}`}
        disabled={!mayPlace} onClick={onPlace}>Place</button>
      {preview?.open && preview.card(li.current)}
    </li>
  );
}

export function NpcsPanel() {
  const { campaignId, table } = useShell();
  const tableState = useOptionalTableState();
  const [shown, setShown] = useState<Shown>({ kind: 'loading' });
  const [q, setQ] = useState('');
  const [placing, setPlacing] = useState<CampaignNpc | null>(null);
  const [cardKey, setCardKey] = useState<string | null>(null);
  const [stars, setStars] = useState<Stars | null>(null);
  const [starring, setStarring] = useState<ReadonlySet<string>>(new Set());
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  // The DM's favourites, read once, when the first card is raised.
  useEffect(() => {
    if (cardKey === null || stars !== null) return;
    setStars('loading');
    Promise.resolve().then(() => compendiumHome(campaignId)).then((groups) => {
      if (!live.current) return;
      setStars(groups ? new Set((groups.find((g) => g.key === 'favourites')?.rows ?? []).map(addressOf)) : 'unknown');
    }).catch(() => { if (live.current) setStars('unknown'); });
  }, [cardKey, stars, campaignId]);
  const say = (text: string) => { if (tableState) tableState.setNotice(text); };
  const star = async (a: { segment: string; slug: string }, name: string) => {
    if (typeof stars === 'string' || stars === null) return;
    const at = addressOf(a);
    const now = !stars.has(at);
    setStarring((s) => new Set(s).add(at));
    const r = await setFavourite(campaignId, a.segment, a.slug, now).catch(() => ({ kind: 'error' as const }));
    if (!live.current) return;
    setStarring((s) => { const next = new Set(s); next.delete(at); return next; });
    if (r.kind === 'ok') {
      setStars((s) => { if (typeof s === 'string' || s === null) return s; const next = new Set(s); if (now) next.add(at); else next.delete(at); return next; });
      say(now ? `${name} is in your favourites.` : `${name} is no longer in your favourites.`);
      return;
    }
    say(r.kind === 'refused' ? r.reason : `${name} could not be ${now ? 'starred' : 'unstarred'} just now. Try again in a moment.`);
  };
  const pageOf = (a: { segment: string; slug: string }) => `${siteUrl()}/${a.segment}?slug=${encodeURIComponent(a.slug)}&system=${encodeURIComponent(table.campaignSystem)}`;
  const openPage = (a: { segment: string; slug: string }) => { window.open(pageOf(a), COMPENDIUM_WINDOW); };
  /** The hover card for an NPC whose stat block the site still has. */
  const previewOf = (n: CampaignNpc): RowPreview | undefined => {
    const a = n.statBlock;
    if (!a || n.statBlockName === null) return undefined;
    const at = addressOf(a);
    return {
      open: cardKey === n.id,
      onOpenChange: (o: boolean) => setCardKey((k) => (o ? n.id : k === n.id ? null : k)),
      card: (anchor: HTMLElement | null) => (
        <EntryCard campaignId={campaignId} segment={a.segment} slug={a.slug} name={n.statBlockName ?? n.name} family="" anchor={anchor}
          starred={typeof stars === 'object' && stars !== null && stars.has(at)} starring={typeof stars !== 'object' || stars === null || starring.has(at)}
          onOpen={() => { setCardKey(null); openPage(a); }}
          onStar={() => { void star(a, n.statBlockName ?? n.name); }}
          onPlace={tableState ? () => { setCardKey(null); setPlacing(n); } : undefined} />
      ),
    };
  };

  const load = useCallback(async () => {
    setShown({ kind: 'loading' });
    const got = await listNpcs(campaignId).catch(() => ({ kind: 'error' as const }));
    if (got.kind === 'ok') setShown({ kind: 'ok', campaignId: got.campaignId, npcs: got.npcs });
    else setShown({ kind: 'error', reason: got.kind === 'refused' ? got.reason : DOWN });
  }, [campaignId]);
  useEffect(() => { void load(); }, [load]);

  const rows = shown.kind === 'ok' ? npcsMatching(shown.npcs, q) : [];
  return (
    <div className="flex h-full flex-col p-2 text-sm" data-testid="npcs">
      <div className="flex gap-2">
        <input type="search" aria-label="Search the campaign's NPCs" data-testid="npcs-search" placeholder="Search by name…" maxLength={100}
          className="min-w-0 flex-1 rounded bg-stone-800 px-2 py-1" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className={btn} aria-label="Refresh the NPC list" data-testid="npcs-refresh" onClick={() => void load()}>Refresh</button>
      </div>
      <div className="mt-2 min-h-0 flex-1 overflow-auto" onScroll={() => setCardKey(null)}>
        {shown.kind === 'loading' && <p data-testid="npcs-state" role="status" className="text-stone-400">Reading the campaign's NPCs…</p>}
        {shown.kind === 'error' && <p data-testid="npcs-state" role="alert" className="text-amber-300">{shown.reason}</p>}
        {shown.kind === 'ok' && shown.npcs.length === 0 && (
          <p data-testid="npcs-state" className="text-stone-400">
            This campaign has no NPCs yet. Add them on the{' '}
            <a className="underline" data-testid="npcs-campaign-link" href={campaignPageUrl(shown.campaignId)} target={COMPENDIUM_WINDOW}>campaign's page on the site</a>
            , then Refresh.
          </p>
        )}
        {shown.kind === 'ok' && shown.npcs.length > 0 && rows.length === 0 && (
          <p data-testid="npcs-state" className="text-stone-400">No NPC's name holds “{q.trim()}”.</p>
        )}
        {rows.length > 0 && (
          <ul className="divide-y divide-stone-800">
            {rows.map((n) => {
              const preview = previewOf(n);
              const a = n.statBlock;
              return (
                <NpcRow key={n.id} n={n} mayPlace={!!tableState} preview={preview}
                  onOpen={preview && a ? () => openPage(a) : undefined}
                  onPlace={() => { setCardKey(null); setPlacing(n); }} />
              );
            })}
          </ul>
        )}
      </div>
      {placing && <PlaceDialog entry={{ segment: '', slug: '', name: placing.name }} npc={{ id: placing.id }} at={null} tracker={false} onClose={() => setPlacing(null)} />}
    </div>
  );
}
